import { type KeyboardEvent as ReactKeyboardEvent } from 'react';

/**
 * IntersectionObserver를 사용하여 스크롤 애니메이션이 완료될 때까지 대기합니다.
 * 요소가 뷰포트에 보이게 되면 resolve됩니다.
 *
 * @param element - 스크롤 대상 요소
 * @returns 요소가 보이면 resolve되는 Promise
 */
const waitForScrollComplete = (element: HTMLElement): Promise<void> => {
  return new Promise(resolve => {
    // IntersectionObserver가 지원되지 않는 브라우저는 무시
    if (window.IntersectionObserver == null) return resolve();
    const observer = new IntersectionObserver(
      entries => {
        if (entries[0].isIntersecting) {
          observer.disconnect();
          resolve();
        }
      },
      { threshold: 0 },
    );

    observer.observe(element);
  });
};

/**
 * 포커스 동작을 제어하는 옵션
 */
export interface FocusOptions {
  /** 요소를 뷰로 스크롤할지 여부 */
  scroll?: boolean;
  /** 포커스하기 전 지연 시간 (ms) */
  delay?: number;
  /** 요소에 포커스할지 여부 */
  focus?: boolean;
}

/**
 * data-focus-id 속성으로 요소를 찾아 포커스합니다.
 * DOM 조작을 제외하고 부수 효과가 없는 순수 함수입니다.
 *
 * @param focusId - 대상 data-focus-id 값
 * @param options - 선택적 포커스 동작 설정
 *
 * @example
 * ```tsx
 * focusById('quizList.0.question', { scroll: true, delay: 100 });
 * ```
 */
const focusById = (focusId: string, options: FocusOptions = {}): void => {
  const { scroll = true, delay = 0, focus = true } = options;

  const execute = async () => {
    const element = document.querySelector(`[data-focus-id="${focusId}"]`);

    if (element instanceof HTMLElement) {
      // 스크롤과 포커스가 모두 활성화된 경우, 순차적으로 실행
      if (scroll && focus) {
        // 먼저 요소로 스크롤
        element.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
        });

        // 스크롤 완료 대기
        await waitForScrollComplete(element);

        // 스크롤 완료 후 클릭
        // 에디터의 focus 이벤트와 겹치지 않기 위해, click 이벤트를 사용합니다.
        element.focus();
      } else {
        // 하나만 활성화되거나 둘 다 비활성화된 경우, 기존 동작 유지
        if (focus) {
          element.focus();
        }

        if (scroll) {
          element.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
          });
        }
      }
    }
  };

  if (delay > 0) {
    setTimeout(() => {
      void execute();
    }, delay);
  } else {
    void execute();
  }
};

/**
 * focus ID를 구성하여 일반 필드에 포커스합니다.
 *
 * @param fieldId - 필드 식별자 (예: "title", "publisher-name")
 * @param options - 선택적 포커스 동작 설정
 *
 * @example
 * ```tsx
 * // 유효성 검사 오류 필드에 포커스
 * if (errors.title) {
 *   focusField('title', { scroll: true });
 * }
 * ```
 */
export const focusField = (
  fieldId: string,
  options: FocusOptions = { scroll: true, focus: true },
): void => {
  focusById(fieldId, options);
};

export const isNextFocusKeyboardEvent = (
  event: KeyboardEvent | ReactKeyboardEvent,
) => {
  return (
    ((event.key === 'Enter' || event.key === 'Tab') && !event.shiftKey) ||
    event.key === 'ArrowDown'
  );
};

export const isPreviousFocusKeyboardEvent = (
  event: KeyboardEvent | ReactKeyboardEvent,
) => {
  return event.key === 'ArrowUp';
};
